/**
 * How dates read across the customer dashboard: headings, drop-downs and the
 * annotations on the weekly order.
 *
 * One flag rather than a choice made per component, because a screen that says
 * "Monday 27th November" in a heading and "Mon 27th Nov" in the drop-down below
 * it reads as two different dates until you look twice.
 */

export type DateStyle = 'short' | 'long'

/**
 * short: "Mon 27th Nov"
 * long:  "Monday 27th November"
 */
export const DATE_STYLE: DateStyle = 'long'

/** 1st, 2nd, 3rd, 21st. 11th to 13th are the exceptions that catch people out. */
export const ordinalSuffix = (day: number): string => {
  if (day % 100 >= 11 && day % 100 <= 13) return 'th'

  switch (day % 10) {
    case 1: return 'st'
    case 2: return 'nd'
    case 3: return 'rd'
    default: return 'th'
  }
}

/**
 * A YYYY-MM-DD string is parsed as local rather than handed to Date, which
 * reads it as UTC and can land on the day before for anyone behind it.
 */
const toDate = (value: Date | string): Date => {
  if (value instanceof Date) return value

  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  return new Date(year, month - 1, day)
}

/** "27th Nov" or "27th November". No weekday: for use after a day name. */
export const formatDayAndMonth = (value: Date | string, style: DateStyle = DATE_STYLE): string => {
  const date = toDate(value)
  const month = date.toLocaleDateString('en-GB', { month: style === 'short' ? 'short' : 'long' })

  return `${date.getDate()}${ordinalSuffix(date.getDate())} ${month}`
}

/** "Mon 27th Nov" or "Monday 27th November". */
export const formatDate = (value: Date | string, style: DateStyle = DATE_STYLE): string => {
  const date = toDate(value)
  const weekday = date.toLocaleDateString('en-GB', { weekday: style === 'short' ? 'short' : 'long' })

  return `${weekday} ${formatDayAndMonth(date, style)}`
}
